import React, { Fragment, useRef } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { MdClose } from 'react-icons/md'
import { useRouter } from 'next/navigation'
import { useEventLogging } from '@packages/jslib'
import { useMutation } from '@tanstack/react-query'
import * as Sentry from '@sentry/nextjs'

import SubmitModal from '@components/SubmitModal/SubmitModal'
import useExamInform from '@hooks/useExamInform'
import { checkDailyQuestionChoice } from '@lib/query/mutateFunction'
import useCurrentQuestion from '@hooks/useCurrentPage'
import useViewResultModeStore from 'store/viewResultMode'
import getQueryForDailyCheck from '@lib/getQueryForDailyCheck'

type CustomModalProps = {
  show: boolean
  setShow: React.Dispatch<React.SetStateAction<boolean>>
  timeout?: boolean
  isDelivery?: boolean
}

const SubmitModalContainer: React.FC<CustomModalProps> = ({
  show,
  setShow,
  timeout,
  isDelivery,
}) => {
  const { trackAction } = useEventLogging()
  const router = useRouter()
  const {
    currentGroupId,
    userChoicesAndWrongsGroup,
    currentQuestionGroup,
    createAnswerResultDailyQuestions,
  } = useExamInform(isDelivery)
  const currentUserChoicesAndWrong = userChoicesAndWrongsGroup[currentGroupId] as Record<
    number,
    { choice?: number; wrong?: number[]; id?: string }
  >
  const { setCurrentPage } = useCurrentQuestion(isDelivery)
  const { setViewResultMode } = useViewResultModeStore()

  const { mutateAsync } = useMutation(checkDailyQuestionChoice())

  const changeDeliveryResultMode = async () => {
    const queryForCheck = getQueryForDailyCheck(currentGroupId, currentUserChoicesAndWrong)
    await mutateAsync(queryForCheck, {
      onSuccess: async (result) => {
        createAnswerResultDailyQuestions(result)
        setCurrentPage(1)
        setViewResultMode(true)
      },
      onError: async (error) => {
        console.error(error)
        Sentry.captureMessage(error as string)
      },
    })
  }

  const totalQuestionCount = currentQuestionGroup?.questions?.length || 80
  const remainQuestionCount = currentUserChoicesAndWrong
    ? Object.values(currentUserChoicesAndWrong).reduce(
        (remainCount: number, userChoicesAndWrongs) => {
          if (userChoicesAndWrongs.choice) {
            return remainCount - 1
          }
          return remainCount
        },
        totalQuestionCount
      )
    : totalQuestionCount

  const CloseModal = () => {
    setShow(false)
  }

  const handleSubmitQuestionInformation = () => {
    trackAction({
      name: 'Submit Answer Sheet',
    })
    CloseModal()
    if (isDelivery) {
      changeDeliveryResultMode()
    } else {
      router.push('/exam/submit')
    }
  }

  const cancelButtonRef = useRef(null)
  const childBackground = 'bg-gray-900'

  const submitModalProps = {
    afterSubmitCallback: handleSubmitQuestionInformation,
    remainQuestionCount: remainQuestionCount,
    CloseModal,
    timeout,
  }

  return (
    <Transition.Root show={show} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        initialFocus={cancelButtonRef}
        onClose={CloseModal}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className={`fixed inset-0 ${childBackground} bg-opacity-50 transition-opacity`} />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full justify-center text-center items-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={
                  'relative transform overflow-hidden rounded-lg bg-white px-6 pt-5 pb-6 text-left shadow-xl transition-all '
                }
              >
                <div className="absolute top-0 right-0 pt-4 pr-4">
                  <button
                    type="button"
                    className="rounded-md bg-white text-black focus:outline-none"
                    onClick={CloseModal}
                  >
                    <span className="sr-only">Close</span>
                    <MdClose size={35} />
                  </button>
                </div>

                <div>
                  <div className="mt-3 text-center sm:mt-5">
                    <div className="mt-2">
                      <div className="max-[640px]:w-[320px] w-[380px]">
                        <SubmitModal {...submitModalProps} />
                      </div>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

export default SubmitModalContainer
